import { Component, Element, Prop, State, Host, h } from '@stencil/core';
import { ACTION_REGISTRY, type ActionName } from '../../../actions';
import type { ActionContext } from '../../../actions/types';

const COMPONENT_NAME = 'rating-type-2';
const STAR_VALUES = [1, 2, 3, 4, 5];

/**
 * @slot heading - {required}, <me-heading variant="5">
 * @slot description - {required}, <me-text>
 */

@Component({
  tag: 'rating-type-2',
  styleUrl: 'index.scss',
  shadow: false,
})
export class RatingType2 {
  @Element() el!: HTMLElement;

  /**
   * Average rating score to display.
   * Example: "4.8".
   * This is the main numeric value shown to represent the overall experience rating.
   */
  @Prop() averageScore!: string;

  /**
   * Short label describing the rating sentiment.
   * Example: "Excellent", "Good", "Satisfied".
   * Used alongside the average score to provide a quick qualitative description.
   */
  @Prop() feedbackStatus!: string;

  /**
   * Total number of votes or reviews.
   * Example: "152 reviews", "1649 votes".
   * This gives context to the average score.
   */
  @Prop() votesQuantity!: string;

  /**
   * Message displayed below the rating after a successful vote.
   * Example: "Thank you for your feedback!"
   */
  @Prop() notificationMessage?: string = 'Thank you for your feedback!';

  /**
   * Whether to load the icon immediately without waiting for it to enter viewport.
   */
  @Prop() isPreload?: boolean = false;

  /**
   * Optional native click handler
   */
  @Prop() clickHandler?: (event: MouseEvent) => void;

  /**
   * Array of action names to execute on star click
   * Format: 'category:action-name'
   * Examples: ['ui:open-modal', 'api:fetch-data']
   */
  @Prop() triggerActions?: ActionName[] = [];

  /**
   * Additional data to pass to actions
   */
  @Prop() actionData?: Record<string, any> = {};

  @State() hoveredRating: number | null = null;
  @State() selectedRating: number | null = null;
  @State() isNotificationVisible: boolean = false;

  private handleMouseEnter(rating: number) {
    if (this.selectedRating !== null) return;
    this.hoveredRating = rating;
  }

  private handleMouseLeave() {
    if (this.selectedRating !== null) return;
    this.hoveredRating = null;
  }

  /**
   * Execute all registered actions
   */
  private async executeActions(event: MouseEvent, rating: number): Promise<void> {
    if (!this.triggerActions?.length) return;

    try {
      for (const actionName of this.triggerActions) {
        await this.executeSingleAction(actionName, event, rating);
      }

      this.isNotificationVisible = true;
    } catch (error) {
      console.error('Action execution failed:', error);
    }
  }

  /**
   * Execute single action
   */
  private async executeSingleAction(actionName: ActionName, event: MouseEvent, rating: number): Promise<void> {
    const actionLoader = ACTION_REGISTRY[actionName];

    if (!actionLoader) {
      console.warn(`Action "${actionName}" not found in registry`);
      return;
    }

    try {
      const actionModule = await actionLoader();
      const ActionClass = actionModule.default;
      const action = new ActionClass();

      const context: ActionContext = {
        event,
        element: this.el,
        component: this,
        data: { ...this.actionData, rating },
      };

      await action.execute(context);
    } catch (error) {
      console.error(`Failed to execute action "${actionName}":`, error);
      throw error;
    }
  }

  /** Handle selecting a rating */
  private async handleStarSelect(rating: number, event?: MouseEvent) {
    if (this.selectedRating !== null) return;
    this.selectedRating = rating;

    if (event && this.triggerActions?.length) {
      await this.executeActions(event, rating);
    }

    if (event && this.clickHandler) {
      this.clickHandler(event);
    }
  }

  private renderRatingStars() {
    const isReadOnly = !!this.selectedRating;

    return STAR_VALUES.map(rating => {
      const isActive =
        (this.hoveredRating !== null && rating <= this.hoveredRating) || (this.hoveredRating === null && this.selectedRating !== null && rating <= this.selectedRating);

      return (
        <me-icon
          key={rating}
          isPreload={this.isPreload}
          name="shapes--star-no-borders"
          class={`
          ${COMPONENT_NAME}__star
          ${isActive ? ` ${COMPONENT_NAME}__star--active` : ''}
          ${isReadOnly ? ` ${COMPONENT_NAME}__star--disabled` : ''}
          `}
          role="radio"
          aria-checked={isActive ? 'true' : 'false'}
          aria-label={`Rate ${rating} star${rating > 1 ? 's' : ''}`}
          aria-disabled={isReadOnly ? 'true' : 'false'}
          tabIndex={0}
          onMouseEnter={() => this.handleMouseEnter(rating)}
          onMouseLeave={() => this.handleMouseLeave()}
          onClick={e => this.handleStarSelect(rating, e)}
          onKeyDown={e => {
            if (!isReadOnly && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              this.handleStarSelect(rating, e as unknown as MouseEvent);
            }
          }}
        />
      );
    });
  }

  render() {
    return (
      <Host class={COMPONENT_NAME}>
        <div>
          <slot name="heading"></slot>
          <slot name="description"></slot>
        </div>
        <div class={`${COMPONENT_NAME}__rating-summary`} itemScope itemType="https://schema.org/AggregateRating">
          <div class={`${COMPONENT_NAME}__feedback-info`}>
            <span class={`${COMPONENT_NAME}__value`} itemProp="ratingValue">
              {this.averageScore}
            </span>
            <span class={`${COMPONENT_NAME}__status`}>{this.feedbackStatus}</span>
          </div>
          <div class={`${COMPONENT_NAME}__stars-info`}>
            <div role="radiogroup" aria-label="Rate your experience" class={`${COMPONENT_NAME}__stars`}>
              {this.renderRatingStars()}
            </div>
            <div class={`${COMPONENT_NAME}__votes`} itemProp="ratingCount">
              {this.votesQuantity}
            </div>
          </div>
          <meta itemProp="bestRating" content="5" />
          <meta itemProp="worstRating" content="1" />
          <div class={`${COMPONENT_NAME}__notification ${this.isNotificationVisible ? 'is-visible' : ''}`}>
            <me-icon class={`${COMPONENT_NAME}__notification-icon`} name="interface--check-circle" />
            <span class={`${COMPONENT_NAME}__notification-msg`}>{this.notificationMessage}</span>
          </div>
        </div>
      </Host>
    );
  }
}
